import { useRef } from 'react'
import styles from './Tabs.module.css'

export interface TabDefinition {
  id: string
  label: string
  /** Optional badge, e.g. how many context entries a tab holds. */
  count?: number
  /** Marks the tab when validation failed inside it. */
  invalid?: boolean
}

interface TabsProps {
  tabs: readonly TabDefinition[]
  active: string
  onChange: (id: string) => void
  label: string
  /** Prefix for the tab/panel id pair, so `panelId` can build matching ids. */
  idPrefix: string
}

export function panelId(idPrefix: string, tabId: string): string {
  return `${idPrefix}-panel-${tabId}`
}

function tabId(idPrefix: string, id: string): string {
  return `${idPrefix}-tab-${id}`
}

/** Tablist with roving tabindex and the expected arrow/Home/End keyboard behaviour. */
export function Tabs({ tabs, active, onChange, label, idPrefix }: TabsProps) {
  const refs = useRef(new Map<string, HTMLButtonElement>())

  const focusTab = (id: string) => {
    onChange(id)
    // Selection follows focus, so the panel and the focused tab stay in step.
    requestAnimationFrame(() => refs.current.get(id)?.focus())
  }

  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    const last = tabs.length - 1
    let next: number | null = null
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = last
    if (next === null) return
    event.preventDefault()
    const target = tabs[next]
    if (target) focusTab(target.id)
  }

  return (
    <div className={styles.tablist} role="tablist" aria-label={label}>
      {tabs.map((tab, index) => (
        <button
          key={tab.id}
          ref={(element) => {
            if (element) refs.current.set(tab.id, element)
            else refs.current.delete(tab.id)
          }}
          type="button"
          role="tab"
          id={tabId(idPrefix, tab.id)}
          aria-selected={tab.id === active}
          aria-controls={panelId(idPrefix, tab.id)}
          tabIndex={tab.id === active ? 0 : -1}
          className={`${styles.tab} ${tab.invalid ? styles.invalid : ''}`}
          onClick={() => onChange(tab.id)}
          onKeyDown={(event) => onKeyDown(event, index)}
        >
          {tab.label}
          {tab.count !== undefined && tab.count > 0 ? <span className={styles.count}>{tab.count}</span> : null}
        </button>
      ))}
    </div>
  )
}

interface TabPanelProps {
  idPrefix: string
  tabId: string
  active: string
  children: React.ReactNode
}

/** Renders only when selected, so opening the editor does not build every panel. */
export function TabPanel({ idPrefix, tabId: id, active, children }: TabPanelProps) {
  if (id !== active) return null
  return (
    <div role="tabpanel" id={panelId(idPrefix, id)} aria-labelledby={`${idPrefix}-tab-${id}`} tabIndex={-1}>
      {children}
    </div>
  )
}
